/**
 * Client for the fleet_state read/write API.
 *
 * Replaces the per-tractor `/v1/assets/{id}` fan-out this page used to do:
 * `GET /v1/vehicles` returns the roster, the config record, the Jira summary,
 * the server-derived `operational_state` and the fleet rollup for every vehicle
 * in one call. The old path issued one request per tractor for a strict subset
 * of that, and could only ever show tractors that already had a Jira asset.
 *
 * Calls go straight to `REACT_APP_FLEET_STATE_API_URL` with the caller's
 * Cognito ID token as a bearer, supplied by the page-scoped login in
 * `src/auth/fleetStateAuth` (SW-6110).
 */
import {
  ApiErrorBody,
  AttrValue,
  ConfigSchema,
  SchemaHistoryEntry,
  SchemaPatch,
  VehicleRow,
  VehiclesResponse,
} from "./configTypes";
import { urlFromEnv } from "../../utils/urlEnv";

// Base URL of the fleet_state HTTP API (scheme+host, no trailing slash). Set
// per-env via the deploy workflow; empty in a plain build (live mode off).
const API_BASE = urlFromEnv(process.env.REACT_APP_FLEET_STATE_API_URL) ?? "";

/**
 * Any non-2xx from the API, carrying the structured body the handler returns.
 *
 * `fields` is populated on a 403 from `ConfigPermissionError`, which names the
 * attributes the caller may not write. Surfacing them matters: writes are
 * all-or-nothing, so an operator whose save was rejected needs to know *which*
 * field cost them the whole edit.
 */
export class FleetStateApiError extends Error {
  readonly status: number;
  readonly fields: string[];

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error || fallback);
    this.name = "FleetStateApiError";
    this.status = status;
    this.fields = body?.fields ?? [];
  }
}

/**
 * 409 — someone else changed the row since we read it.
 *
 * Carries the row *as it now stands* (the handler returns it precisely so the
 * client need not re-fetch to explain the conflict), letting the UI show what
 * changed underneath the operator instead of a bare "try again".
 */
export class ConfigConflictError extends Error {
  readonly current: VehicleRow;

  constructor(message: string, current: VehicleRow) {
    super(message);
    this.name = "ConfigConflictError";
    this.current = current;
  }
}

async function readBody(
  res: Response,
): Promise<Record<string, unknown> | null> {
  try {
    return await res.json();
  } catch {
    // A gateway-level failure (502/504) isn't JSON. Not an error worth
    // reporting on its own — the status is what the caller acts on.
    return null;
  }
}

async function request<T>(
  path: string,
  token: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (res.ok) return (await res.json()) as T;

  const body = await readBody(res);
  if (res.status === 409 && body && typeof body.current === "object") {
    throw new ConfigConflictError(
      String(body.error ?? "This tractor changed while you were editing it."),
      body.current as VehicleRow,
    );
  }
  throw new FleetStateApiError(
    res.status,
    body as ApiErrorBody | null,
    `${init?.method ?? "GET"} ${path} → ${res.status}`,
  );
}

/**
 * The field/vocabulary contract, resolved for this caller.
 *
 * Fetched once per session and threaded through the views. Every label, enum
 * option, glyph, ordering, staleness threshold and edit affordance the console
 * renders comes from here — so the UI cannot offer an edit the write path would
 * reject, and cannot drift from the validation behind it.
 */
export const fetchConfigSchema = (token: string): Promise<ConfigSchema> =>
  request<ConfigSchema>("/v1/config-schema", token);

/** A schema edit lost a race: the body's `current` is the schema as it stands. */
export class SchemaConflictError extends Error {
  current: ConfigSchema;
  constructor(message: string, current: ConfigSchema) {
    super(message);
    this.name = "SchemaConflictError";
    this.current = current;
  }
}

/**
 * Change the schema's vocabulary and presentation — fleet-admins only.
 *
 * Per-field replace (see SchemaPatch). The response is the whole schema at its
 * new version, which the caller should adopt as-is. A 409 surfaces as a
 * SchemaConflictError carrying the current schema, so the editor can reload
 * and let the admin re-apply their change rather than overwrite someone's.
 */
export async function patchConfigSchema(
  token: string,
  body: SchemaPatch,
): Promise<ConfigSchema> {
  try {
    return await request<ConfigSchema>("/v1/config-schema", token, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  } catch (e) {
    if (e instanceof ConfigConflictError) {
      throw new SchemaConflictError(
        e.message,
        e.current as unknown as ConfigSchema,
      );
    }
    throw e;
  }
}

export const fetchSchemaHistory = (
  token: string,
): Promise<{ history: SchemaHistoryEntry[] }> =>
  request<{ history: SchemaHistoryEntry[] }>(
    "/v1/config-schema/history",
    token,
  );

/** The whole fleet — roster + config + Jira summary + rollup, in one call. */
export const fetchVehicles = (token: string): Promise<VehiclesResponse> =>
  request<VehiclesResponse>("/v1/vehicles", token);

/** One vehicle: everything the grid has, plus open tickets and config history. */
export const fetchVehicle = (
  token: string,
  vehicleName: string,
): Promise<VehicleRow> =>
  request<VehicleRow>(`/v1/vehicles/${encodeURIComponent(vehicleName)}`, token);

/**
 * Onboard a tractor — create the roster row the rest of the system keys on.
 *
 * A taken name comes back as a 409 rather than an overwrite, so the caller can
 * say "that one already exists" instead of silently replacing a live tractor's
 * roster row. Fleet-admin only, enforced by the API through the same
 * `editable_by` policy that governs edits.
 */
export function createVehicle(
  token: string,
  vehicleName: string,
  fields: Record<string, AttrValue>,
): Promise<VehicleRow> {
  return request<VehicleRow>("/v1/vehicles", token, {
    method: "POST",
    body: JSON.stringify({ ...fields, vehicle_name: vehicleName }),
  });
}

/**
 * Submit one edit.
 *
 * `changes` must already be the dirty subset — sending an unchanged field is
 * harmless (the handler drops no-op deltas when building the change-set) but it
 * widens the set of attributes the permission check runs against, and a write
 * is refused wholesale if *any* named field is forbidden. So an operator who
 * merely opened a form containing an admin-only field would be rejected for an
 * edit they never made. Send only what actually changed.
 *
 * `expectedVersion` is the `config_version` that was read. It is omitted rather
 * than sent as null for a never-edited row: the handler reads an absent version
 * as "assert nobody has edited this yet", which is exactly right, and sending
 * an explicit null to mean the same thing is a coincidence of `dict.pop`
 * semantics rather than a contract worth relying on.
 *
 * `confirm` names attestations being re-affirmed at their current value. It is a
 * separate list rather than a same-value edit because a same-value edit is a
 * no-op the API drops: re-affirming has to be stated, not inferred. Both ride in
 * one request so a save that edits some fields and re-confirms others is still
 * one change-set, one version bump, and one all-or-nothing write.
 */
export interface PatchOptions {
  /** When the change took effect, ISO 8601 with an offset; null means now. */
  effectiveAt?: string | null;
  /** The one-line why; blank sends nothing. */
  note?: string | null;
}

export function patchVehicle(
  token: string,
  vehicleName: string,
  changes: Record<string, AttrValue>,
  expectedVersion: number | null,
  confirm: string[] = [],
  options: PatchOptions = {},
): Promise<VehicleRow> {
  const body: Record<string, unknown> = { ...changes };
  if (typeof expectedVersion === "number") {
    body.config_version = expectedVersion;
  }
  if (confirm.length) {
    body.confirm = confirm;
  }
  // Both are control keys beside the field edits (SW-8879). Sent only when
  // set, so an API that predates them sees the body it always saw.
  if (options.effectiveAt) {
    body.effective_at = options.effectiveAt;
  }
  const note = options.note?.trim();
  if (note) {
    body.note = note;
  }
  return request<VehicleRow>(
    `/v1/vehicles/${encodeURIComponent(vehicleName)}`,
    token,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}
